import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { ProgressIndicator } from "@asosteam/asos-web-component-library-progress-indicator";
import VerifyStudentContainer from "./containers/verify-student";
import { VerificationDetailsProvider } from "./context/verification-details";
import { LocaleProvider } from "./context/locale-provider";
import { verificationDetailsReducer } from "./state/verification-details/reducer";
import styles from "./styles";
import ViewDiscount from "./containers/view-discount";
import { Banner } from "./components/banner/index";
import PreFormContainer from "./containers/pre-form";
import { fetchStudentStatus } from "./utilities/fetch-student-status";
import { fetchConfig } from "./utilities/fetch-config";
import { requestHelper } from "./utilities/request-helper";
import { verificationCustomerStatus } from "./constants/verification-status";
import { isAuthenticated } from "./containers/utilities";

export default function App({ appData }) {
  const { page, payload, storeData } = appData;
  const [loading, setLoading] = useState(true);
  const [authSuccess, setAuthSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(page);
  const [studentCode, setStudentCode] = useState();
  const [customerStatus, setCustomerStatus] = useState();
  const [availableCountries, setAvailableCountries] = useState();
  const [minGraduationYear, setMinGraduationYear] = useState();
  const [minDateOfBirth, setMinDateOfBirth] = useState();
  const [discountInternalError, setDiscountInternalError] = useState();
  const [externalCustomerVerificationUrl, setExternalCustomerVerificationUrl] =
    useState();

  useEffect(() => {
    const authStatus = async () => {
      const authResult = await isAuthenticated();
      setAuthSuccess(authResult);
      if (!authResult) {
        setLoading(false);
      }
    };

    authStatus();
  }, []);

  const displayDiscountViewInternalError = () => {
    if (currentPage === "discount") {
      setDiscountInternalError(true);
    }
  };

  const isCustomerSuccess = (response) =>
    currentPage === "verification" &&
    (response.status === verificationCustomerStatus.ACTIVE ||
      response.status === verificationCustomerStatus.PENDING_DISCOUNT);

  const isCustomerError = (response) =>
    currentPage === "discount" &&
    response.status === verificationCustomerStatus.PENDING_VERIFICATION;

  useEffect(() => {
    const getStudentStatus = async () => {
      try {
        const response = await fetchStudentStatus(requestHelper);
        setStudentCode(response.discountCode);
        setCustomerStatus(response?.status);
        setExternalCustomerVerificationUrl(response?.externalVerificationUrl);
        if (isCustomerSuccess(response)) {
          window.history.replaceState(
            window.history.state,
            "",
            storeData.successRedirectPath
          );
          setCurrentPage("discount");
        }
        if (isCustomerError(response)) {
          displayDiscountViewInternalError();
        }
      } catch (e) {
        displayDiscountViewInternalError();
      }
      setLoading(false);
    };

    const getConfig = async () => {
      const response = await fetchConfig(requestHelper);
      setAvailableCountries(response.countries);
      setMinDateOfBirth(response.minDateOfBirth);
      setMinGraduationYear(response.minGraduationYear);

      setLoading(false);
    };

    if (authSuccess) {
      setLoading(true);
      getStudentStatus();
      getConfig();
    }
  }, [authSuccess]);

  const displayVerificationErrorPage = () => {
    window.history.pushState(
      window.history.state,
      "",
      storeData.successRedirectPath
    );
    setCurrentPage("discount");
    setCustomerStatus("error");
  };

  if (loading) {
    return (
      <>
        <Banner />
        <div className={styles.container}>
          <div className={styles.layout}>
            <div className={styles.progressContainer}>
              <ProgressIndicator
                progressMessage="loading..."
                size="large"
                transparent
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (currentPage === "verification") {
    const { orgSearchUrl, sheerIdProgramId, countriesData, assetsUrl } =
      payload;
    return (
      <LocaleProvider languageStrings={storeData.languageStrings}>
        <Banner />
        <div className={styles.container}>
          <div className={styles.layout}>
            {authSuccess ? (
              <VerificationDetailsProvider
                initialState={{
                  loading: true,
                  hasError: false,
                  verificationDetails: {
                    firstName: "",
                    lastName: "",
                    dateOfBirth: {},
                    organisation: {
                      name: "",
                      id: "",
                    },
                    countryCode: "",
                    graduationYear: "",
                  },
                }}
                reducer={verificationDetailsReducer}
              >
                <VerifyStudentContainer
                  successRedirectPath={storeData.successRedirectPath}
                  orgSearchUrl={orgSearchUrl}
                  countriesData={countriesData}
                  sheerIdProgramId={sheerIdProgramId}
                  assetsUrl={assetsUrl}
                  displayVerificationErrorPage={displayVerificationErrorPage}
                  customerStatus={customerStatus}
                  availableCountries={availableCountries}
                  minDateOfBirth={minDateOfBirth}
                  minGraduationYear={minGraduationYear}
                  externalCustomerVerificationUrl={
                    externalCustomerVerificationUrl
                  }
                  sheerIdLocale={storeData.sheerIdLocale}
                />
              </VerificationDetailsProvider>
            ) : (
              <PreFormContainer />
            )}
          </div>
        </div>
      </LocaleProvider>
    );
  } else if (currentPage === "discount") {
    const { sheerIdProgramId } = payload;
    return (
      <LocaleProvider languageStrings={storeData.languageStrings}>
        <Banner />
        <ViewDiscount
          sheerIdProgramId={sheerIdProgramId}
          studentCode={studentCode}
          customerStatus={customerStatus}
          discountInternalError={discountInternalError}
          sheerIdLocale={storeData.sheerIdLocale}
        />
      </LocaleProvider>
    );
  }
}

App.propTypes = {
  appData: PropTypes.object,
};

App.defaultProps = {
  appData: {
    page: "",
    payload: {},
  },
};
